import { useState } from "react";
import { Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { whatsappLink } from "@/config/site";
import { track } from "@/lib/analytics";

const PROPERTY_TYPES = ["Studio", "Apartment", "House", "Office or other", "Mixed / portfolio"];
const SERVICES = ["End of tenancy cleaning", "Deep cleaning", "Move-in cleaning", "Apartment cleaning", "House cleaning", "Oven cleaning", "Carpet cleaning", "Rubbish removal", "Furniture removal", "Office / common-area cleaning"];
const WORK_TYPES = ["One-off", "Recurring", "Not sure yet"];

const inputCls = "h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20 focus-visible:border-emerald-600";

const Field = ({ label, required, children, testid }) => (
  <div className="flex flex-col gap-1.5" data-testid={testid}>
    <Label className="text-sm font-semibold text-slate-700">{label} {required && <span className="text-emerald-600">*</span>}</Label>
    {children}
  </div>
);

export const LandlordForm = () => {
  const [form, setForm] = useState({
    company_name: "", contact_person: "", phone: "", email: "", property_address: "",
    property_type: "", bedrooms: "", service_required: "", preferred_date: "", work_type: "",
    num_properties: "", message: "", company: "",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.contact_person.trim()) e.contact_person = "Required";
    if (!/^[+\d][\d\s()-]{5,}$/.test(form.phone.trim())) e.phone = "Enter a valid phone";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!consent) e.consent = "Please tick the consent box";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      await api.post("/landlord-enquiries", { ...form, consent, source_page: "/landlords-estate-agents" });
      track.formSubmit("landlord-agent");
      setDone(true);
      window.scrollTo({ top: document.getElementById("landlord-form")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try WhatsApp.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div id="landlord-form" className="rounded-3xl border border-emerald-200 bg-white p-10 text-center shadow-xl" data-testid="landlord-success">
        <CheckCircle2 size={52} className="mx-auto text-emerald-600" />
        <h3 className="mt-4 font-display text-2xl font-black text-slate-900">Thank you</h3>
        <p className="mt-2 text-slate-600">We've received your enquiry and will be in touch to discuss your property cleaning and turnover needs. For a faster response, message us on WhatsApp.</p>
        <a href={whatsappLink("Hello, I'm a landlord / estate agent and would like a quote for property cleaning.")} onClick={() => track.whatsappClick("landlord-success")} target="_blank" rel="noreferrer"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-bold text-white transition-transform hover:scale-105">
          <MessageCircle size={18} /> Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form id="landlord-form" onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-9" data-testid="landlord-form" noValidate>
      <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Company / Agency name" testid="lf-company"><Input data-testid="lf-company" className={inputCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} /></Field>
        <Field label="Contact person" required testid="lf-contact"><Input data-testid="lf-contact" className={inputCls} value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />{errors.contact_person && <span className="text-xs text-red-500">{errors.contact_person}</span>}</Field>
        <Field label="Phone" required testid="lf-phone"><Input data-testid="lf-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />{errors.phone && <span className="text-xs text-red-500">{errors.phone}</span>}</Field>
        <Field label="Email" required testid="lf-email"><Input data-testid="lf-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />{errors.email && <span className="text-xs text-red-500">{errors.email}</span>}</Field>
        <Field label="Property address" testid="lf-address"><Input data-testid="lf-address" className={inputCls} value={form.property_address} onChange={(e) => set("property_address", e.target.value)} /></Field>
        <Field label="Property type" testid="lf-type">
          <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
            <SelectTrigger data-testid="lf-type-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Number of bedrooms" testid="lf-bedrooms"><Input data-testid="lf-bedrooms" className={inputCls} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} inputMode="numeric" placeholder="e.g. 2" /></Field>
        <Field label="Service required" testid="lf-service">
          <Select value={form.service_required} onValueChange={(v) => set("service_required", v)}>
            <SelectTrigger data-testid="lf-service-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{SERVICES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Preferred date" testid="lf-date"><Input data-testid="lf-date" type="date" className={inputCls} value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} /></Field>
        <Field label="One-off or recurring work" testid="lf-worktype">
          <Select value={form.work_type} onValueChange={(v) => set("work_type", v)}>
            <SelectTrigger data-testid="lf-worktype-select" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{WORK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Number of properties managed" testid="lf-numprops"><Input data-testid="lf-numprops" className={inputCls} value={form.num_properties} onChange={(e) => set("num_properties", e.target.value)} placeholder="e.g. 1, 5, 20+" /></Field>
      </div>
      <div className="mt-5">
        <Field label="Message" testid="lf-message"><Textarea data-testid="lf-message" className="min-h-24 rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-emerald-600/20" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about the property/portfolio, turnover needs, access, timelines, etc." /></Field>
      </div>
      <label className="mt-6 flex items-start gap-3 text-sm text-slate-600">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="lf-consent" className="mt-0.5 data-[state=checked]:bg-emerald-700 data-[state=checked]:border-emerald-700" />
        <span>I agree to be contacted regarding my property cleaning enquiry.</span>
      </label>
      {errors.consent && <span className="mt-1 block text-xs text-red-500">{errors.consent}</span>}
      <button type="submit" disabled={submitting} data-testid="lf-submit" className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-700 px-8 py-4 text-base font-bold text-white transition-all hover:bg-emerald-800 disabled:opacity-60">
        {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Request a Landlord / Agent Quote"}
      </button>
    </form>
  );
};
